// src/pages/MyPage.js — 마이페이지: 기본 정보 · 신체 정보 · 영양 목표 (목표 저장 후 식단 추천으로 안내)
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserInfo, updateUserInfo } from '../api/dietApi';
import { useToast } from '../components/ui/Toast';
import { fmt } from '../components/ui/Nutrients';
import { ACT_LV, RATE_PRESETS, rateToGrams, recommendKcal } from './goal';
import './MyPage.css';

const FIELDS = ['userId', 'userName', 'gender', 'birthDate', 'phoneNo', 'email', 'height', 'weight', 'actLv', 'wantKcal', 'carboRate', 'protRate', 'fatRate'];
const GOAL_KEYS = ['wantKcal', 'carboRate', 'protRate', 'fatRate'];

const toForm = (d) => {
  const f = {};
  FIELDS.forEach((k) => { f[k] = d?.[k] === null || d?.[k] === undefined ? '' : String(d[k]); });
  f.birthDate = f.birthDate ? f.birthDate.substring(0, 10) : '';
  return f;
};

export default function MyPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [info, setInfo] = useState(null);
  const [draft, setDraft] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getUserInfo()
      .then((r) => { const f = toForm(r.data); setInfo(f); setDraft(f); })
      .catch(() => setError('회원 정보를 불러오지 못했습니다.'));
  }, []);

  const view = editing ? draft : info;
  const set = (k, v) => setDraft((p) => ({ ...p, [k]: v }));

  const rateSum = useMemo(() => (view ? ['carboRate', 'protRate', 'fatRate'].reduce((s, k) => s + (Number(view[k]) || 0), 0) : 0), [view]);
  const goalFilled = view && GOAL_KEYS.every((k) => view[k] !== '');
  const grams = goalFilled ? rateToGrams(view.wantKcal, view.carboRate, view.protRate, view.fatRate) : null;
  const suggested = view ? recommendKcal(view) : null;

  if (error) return <p className="mp-msg error">{error}</p>;
  if (!info) return <p className="mp-msg">불러오는 중…</p>;

  const save = async () => {
    const anyGoal = GOAL_KEYS.some((k) => draft[k] !== '');
    if (anyGoal && !GOAL_KEYS.every((k) => draft[k] !== '')) { toast('영양 목표는 희망 칼로리와 비율 3개를 모두 입력해 주세요.', { type: 'error' }); return; }
    if (anyGoal && rateSum !== 100) { toast(`탄수화물·단백질·지방 비율의 합이 100%여야 합니다. (지금 ${rateSum}%)`, { type: 'error' }); return; }
    setSaving(true);
    try {
      const payload = { ...draft };
      ['height', 'weight', 'actLv', ...GOAL_KEYS].forEach((k) => { payload[k] = payload[k] === '' ? null : Number(payload[k]); });
      await updateUserInfo(payload);
      const goalChanged = GOAL_KEYS.some((k) => draft[k] !== info[k]);
      setInfo(draft);
      setEditing(false);
      if (goalChanged && goalFilled) {
        toast('영양 목표를 저장했어요. 이제 목표에 맞춘 식단을 추천받을 수 있어요.', {
          type: 'success', action: { label: '식단 추천 받기', onClick: () => navigate('/DietRecommend') },
        });
      } else {
        toast('저장했습니다.', { type: 'success' });
      }
    } catch (e) {
      toast(e?.response?.data?.message || (typeof e?.response?.data === 'string' ? e.response.data : '') || '저장에 실패했습니다.', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };


  const genderLabel = (g) => (g === '0' ? '남성' : g === '1' ? '여성' : '-');
  const actLabel = (v) => { const a = ACT_LV.find((x) => x.v === v); return a ? `${a.l} (${a.d})` : '-'; };

  const row = (label, value, input) => (
    <div className="mp-row">
      <dt>{label}</dt>
      <dd>{editing && input ? input : (value || <span className="mp-empty">비어 있음</span>)}</dd>
    </div>
  );
  const text = (k, type = 'text', extra = {}) => (
    <input className="input" type={type} value={draft[k]} onChange={(e) => set(k, e.target.value)} {...extra} />
  );

  return (
    <div className="mp">
      <div className="mp-head">
        <div>
          <h2>{info.userName || info.userId}</h2>
          <p>{info.userId}</p>
        </div>
        {editing ? (
          <div className="mp-actions">
            <button className="btn ghost" type="button" onClick={() => { setDraft(info); setEditing(false); }}>취소</button>
            <button className="btn primary" type="button" disabled={saving} onClick={save}>{saving ? '저장 중…' : '저장'}</button>
          </div>
        ) : (
          <button className="btn" type="button" onClick={() => setEditing(true)}>수정</button>
        )}
      </div>

      <section className="mp-sec">
        <h3>영양 목표</h3>
        {!goalFilled && !editing && (
          <div className="mp-goal-cta">
            <span>목표를 정하면 오늘 섭취량과 맞춤 식단 추천을 볼 수 있어요.</span>
            <button className="btn primary" type="button" onClick={() => setEditing(true)}>목표 입력하기</button>
          </div>
        )}
        <dl>
          {row('희망 칼로리', view.wantKcal && `${fmt(view.wantKcal)} kcal / 하루`, (
            <div className="mp-inline">
              {text('wantKcal', 'number', { min: 0, placeholder: '예: 2000' })}<em>kcal</em>
              {suggested && <button className="btn ghost" type="button" onClick={() => set('wantKcal', String(suggested))}>권장 {fmt(suggested)} kcal 넣기</button>}
            </div>
          ))}
          {row('탄 · 단 · 지 비율', goalFilled && `${view.carboRate}% · ${view.protRate}% · ${view.fatRate}%`, (
            <div className="mp-rates">
              <label><span style={{ color: 'var(--carbo)' }}>탄</span>{text('carboRate', 'number', { min: 0, max: 100 })}%</label>
              <label><span style={{ color: 'var(--protein)' }}>단</span>{text('protRate', 'number', { min: 0, max: 100 })}%</label>
              <label><span style={{ color: 'var(--fat)' }}>지</span>{text('fatRate', 'number', { min: 0, max: 100 })}%</label>
              <span className={`mp-sum${rateSum === 100 ? ' ok' : ''}`}>합 {rateSum}%</span>
              <div className="mp-presets">
                {RATE_PRESETS.map((p) => (
                  <button key={p.label} type="button" className="chip" onClick={() => setDraft((d) => ({ ...d, carboRate: String(p.c), protRate: String(p.p), fatRate: String(p.f) }))}>{p.label}</button>
                ))}
              </div>
            </div>
          ))}
          {grams && (
            <div className="mp-row">
              <dt>하루 목표량</dt>
              <dd className="mp-grams">
                <span style={{ color: 'var(--carbo)' }}>탄수 {fmt(grams.carbo)}g</span>
                <span style={{ color: 'var(--protein)' }}>단백 {fmt(grams.protein)}g</span>
                <span style={{ color: 'var(--fat)' }}>지방 {fmt(grams.fat)}g</span>
              </dd>
            </div>
          )}
        </dl>
      </section>

      <section className="mp-sec">
        <h3>신체 정보</h3>
        <dl>
          {row('키', view.height && `${view.height} cm`, <div className="mp-inline">{text('height', 'number', { min: 0 })}<em>cm</em></div>)}
          {row('몸무게', view.weight && `${view.weight} kg`, <div className="mp-inline">{text('weight', 'number', { min: 0 })}<em>kg</em></div>)}
          {row('활동량', view.actLv && actLabel(view.actLv), (
            <select className="select" value={draft.actLv} onChange={(e) => set('actLv', e.target.value)}>
              <option value="">선택</option>
              {ACT_LV.map((a) => <option key={a.v} value={a.v}>{a.l} ({a.d})</option>)}
            </select>
          ))}
        </dl>
      </section>

      <section className="mp-sec">
        <h3>기본 정보</h3>
        <dl>
          {row('아이디', info.userId)}
          {row('이름', view.userName, text('userName'))}
          {row('성별', genderLabel(view.gender), (
            <select className="select" value={draft.gender} onChange={(e) => set('gender', e.target.value)}>
              <option value="0">남성</option><option value="1">여성</option>
            </select>
          ))}
          {row('생년월일', view.birthDate, text('birthDate', 'date'))}
          {row('휴대폰', view.phoneNo, text('phoneNo', 'tel', { inputMode: 'numeric' }))}
          {row('이메일', view.email, text('email', 'email'))}
        </dl>
      </section>
    </div>
  );
}
